import * as React from "react";
import { CornerDownRight, FileInput, Languages, ShieldAlert, Users, FileTextIcon } from "lucide-react";
import { FileItem } from "@/types/fse";
import { useLazyGetDocumentGroupsDetailsQuery } from "@/modules/documentGroup/documentGroup.api";
import TopBarProgress from "react-topbar-progress-indicator";
import clsx from "clsx";
import { Tooltip } from "@/components/ui/tooltip";
import { useRecentItems } from "@/hooks/use-add-recent-items";
import { getRejectionLabel } from "../rejectionUtils";

interface Props {
  item: FileItem;
  isShared?: boolean;
  isDragging: boolean;
}

export const DriveRowNameFile: React.FC<Props> = ({ item, isShared, isDragging }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const { addRecentFile } = useRecentItems();

  const [getDocumentGroupsDetails, { data: groupsDetails, isLoading }] = useLazyGetDocumentGroupsDetailsQuery();

  const handleMouseOver = () => {
    if (item.type === "file" && item.documents_groups?.length && !groupsDetails) {
      getDocumentGroupsDetails(item.documents_groups);
    }
  };

  const handleOnClick = () => {
    setShowDetails(!showDetails);
    // Track file access for recent items
    addRecentFile(item);
  };
  const filename = (
    <span
      className="block min-w-0 flex-1 truncate"
      title={item.name}
      aria-label={item.name}
      tabIndex={isDragging ? -1 : 0}
    >
      {item.name}
    </span>
  );

  return (
    <div className="min-w-0">
      <div
        className={clsx("flex min-w-0 items-center", {
          "cursor-pointer": !!item.documents_groups?.length,
        })}
        onClick={handleOnClick}
        onMouseOver={handleMouseOver}
      >
        {!item.documents_groups?.length ? (
          <FileTextIcon className="mr-2 h-5 w-5 shrink-0 text-blue-600" />
        ) : (
          <FileInput className="mr-2 h-5 w-5 shrink-0 text-sky-600" />
        )}
        <span className="flex min-w-0 flex-1 items-center font-medium">
          {isDragging ? (
            filename
          ) : (
            <Tooltip text={item.name} className="rounded bg-black text-xs text-white" delayDuration={50}>
              {filename}
            </Tooltip>
          )}
          {isShared && <Users className="ml-2 h-3 w-3 shrink-0 text-gray-500" />}
          {item.original_document_id && (
            <>
              <Languages className="ml-2 h-3 w-3 shrink-0 text-gray-500" />{" "}
              <span className="shrink-0 text-sm text-gray-500">Translated</span>
            </>
          )}
          {item.rejection_reason && (
            <Tooltip
              text={getRejectionLabel(item.rejection_reason)}
              className="rounded bg-black text-xs text-white"
              delayDuration={50}
            >
              <span className="ml-2 flex shrink-0 items-center text-sm text-red-500">
                <ShieldAlert className="mr-1 h-3 w-3" />
                Rejected
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      {showDetails && (
        <>
          {isLoading && <TopBarProgress />}

          {groupsDetails &&
            groupsDetails.map(groupDetails => (
              <div className="p-2" key={groupDetails.parent.id}>
                <div className="mb-1 text-sm text-gray-600">Found in:</div>
                <div className="mb-1 ml-2 flex items-center">
                  <CornerDownRight className="mr-2 h-4 w-4 text-gray-600" />
                  <span className="text-sm" title={groupDetails.parent.id}>
                    {groupDetails.parent.name}
                  </span>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
};
